import type * as React from "react";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from "@ando/ui";
import type { AdminOrganization } from "@ando/core";

import {
  adminOrgKeys,
  useDeactivateAdminOrg,
  useReactivateAdminOrg,
} from "@/features/admin/hooks/useAdminOrg";

/**
 * DeactivateOrgDialog — soft-toggle ``is_active`` via DELETE (deactivate)
 * or POST ``/reactivate``. The same component renders both intents and the
 * caller picks via ``mode`` (driven by ``org.is_active``).
 *
 * Soft-deactivate is reversible — members keep their data, but the org is
 * hidden from billing/listing and no new logins succeed. Hard-delete is a
 * separate concern (see ``HardDeleteOrgDialog``).
 */

export interface DeactivateOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: AdminOrganization;
}

export function DeactivateOrgDialog({
  open,
  onOpenChange,
  org,
}: DeactivateOrgDialogProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();
  const deactivate = useDeactivateAdminOrg(org.id);
  const reactivate = useReactivateAdminOrg(org.id);

  // ``is_active`` drives intent — same surface, two verbs. Looking at the
  // org snapshot at dialog open time keeps the labels stable while the
  // mutation flies; the post-success invalidate will re-render with the
  // flipped state for the next open.
  const willDeactivate = org.is_active;
  const isPending = willDeactivate
    ? deactivate.isPending
    : reactivate.isPending;

  const handleConfirm = (): void => {
    const onSuccess = (): void => {
      toast.success(
        willDeactivate
          ? i18n._(msg`Organizzazione disattivata`)
          : i18n._(msg`Organizzazione riattivata`),
        { description: org.name },
      );
      qc.invalidateQueries({ queryKey: adminOrgKeys.list });
      onOpenChange(false);
    };
    const onError = (err: unknown): void => {
      toast.error(
        willDeactivate
          ? i18n._(msg`Disattivazione fallita`)
          : i18n._(msg`Riattivazione fallita`),
        {
          description:
            err instanceof Error ? err.message : i18n._(msg`Errore inatteso.`),
        },
      );
    };
    if (willDeactivate) {
      deactivate.mutate(undefined, { onSuccess, onError });
    } else {
      reactivate.mutate(undefined, { onSuccess, onError });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {willDeactivate
              ? i18n._(msg`Disattivare «${org.name}»?`)
              : i18n._(msg`Riattivare «${org.name}»?`)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {willDeactivate
              ? i18n._(
                  msg`I membri non potranno più accedere finché l'organizzazione resta disattivata. L'operazione è reversibile — i dati restano intatti.`,
                )
              : i18n._(
                  msg`Riattivando l'organizzazione i membri tornano a poter accedere immediatamente. Nessun dato è stato cancellato durante la disattivazione.`,
                )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {i18n._(msg`Annulla`)}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Stop Radix' default close-on-action; we drive the close
              // ourselves so the dialog stays open during the round-trip.
              e.preventDefault();
              handleConfirm();
            }}
            disabled={isPending}
            className={
              willDeactivate
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : undefined
            }
          >
            {isPending ? (
              <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
            ) : null}
            {willDeactivate ? i18n._(msg`Disattiva`) : i18n._(msg`Riattiva`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
